const FAIR_DISTANCE_EPSILON = 1e-9;

export interface OverUnderMarketCandidate {
    id: number | string;
    overPrice: number | null;
    underPrice: number | null;
    websiteMain: boolean;
    displayOrder: number;
    index: number;
}

const isValidDecimalPrice = (value: number | null): value is number => {
    return typeof value === 'number' && Number.isFinite(value) && value > 0;
};

export const getFairProbabilityDistance = (overPrice: number | null, underPrice: number | null): number | null => {
    if (isValidDecimalPrice(overPrice) === false || isValidDecimalPrice(underPrice) === false) {
        return null;
    }

    const overImpliedProbability = 1 / overPrice;
    const underImpliedProbability = 1 / underPrice;
    const marketProbability = overImpliedProbability + underImpliedProbability;

    if (Number.isFinite(marketProbability) === false || marketProbability <= 0) {
        return null;
    }

    const fairOverProbability = overImpliedProbability / marketProbability;
    return Math.abs(fairOverProbability - 0.5);
};

export const selectBestOverUnderMarketCandidate = (
    candidates: Array<OverUnderMarketCandidate>
): OverUnderMarketCandidate | null => {
    let bestCandidate: OverUnderMarketCandidate | null = null;
    let bestDistance: number | null = null;

    for (const candidate of candidates) {
        const distance = getFairProbabilityDistance(candidate.overPrice, candidate.underPrice);

        if (distance === null) {
            continue;
        }

        if (bestCandidate === null || bestDistance === null) {
            bestCandidate = candidate;
            bestDistance = distance;
            continue;
        }

        if (distance + FAIR_DISTANCE_EPSILON < bestDistance) {
            bestCandidate = candidate;
            bestDistance = distance;
            continue;
        }

        if (Math.abs(distance - bestDistance) > FAIR_DISTANCE_EPSILON) {
            continue;
        }

        if (candidate.websiteMain !== bestCandidate.websiteMain) {
            if (candidate.websiteMain === true) {
                bestCandidate = candidate;
                bestDistance = distance;
            }
            continue;
        }

        if (candidate.displayOrder !== bestCandidate.displayOrder) {
            if (candidate.displayOrder < bestCandidate.displayOrder) {
                bestCandidate = candidate;
                bestDistance = distance;
            }
            continue;
        }

        if (candidate.index < bestCandidate.index) {
            bestCandidate = candidate;
            bestDistance = distance;
        }
    }

    return bestCandidate;
};
